import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BaseButton } from "src/components/ui/baseButton";
import moment from "moment";
import MainInformation from "./components/MainInformation";
import {
  AirConditionerListsForm,
  AuthorsAndContributorListsForm,
  OrganizationalScopeForm,
  OrganizationChartForm,
} from "./components/cfoReport";
import clsx from "clsx";
import CheckBoxListsForm from "./components/cfoReport/CheckBoxListsForm";
import {
  createCfoReportFactSheet,
  useGetCfoReportFactSheet,
} from "../services/useFactSheetApi";
import { useDempStore } from "src/context/stores";
import dayjs from "dayjs";
import FiscalYearForm from "./components/cfoReport/FiscalYearForm";
import { groupOptionRefrigerant } from "./components/cfoReport/groupOptionData";

const schema = yup.object({
  airConditionerList: yup.array().of(
    yup.object().shape({
      airConditionerBTU: yup.string().required("This is a required field"),
      airConditionerRefrigerant: yup
        .string()
        .required("This is a required field"),
      airConditionerAmount: yup.string().required("This is a required field"),
    })
  ),
  fiscalYear: yup.object().shape({
    startDate: yup.object().required("This is a required field"),
    endDate: yup.object().required("This is a required field"),
  }),
  //   scope1: yup.array().required("This is a required field"),
});

const defaultGeneralFactSheet = {
  companyImage: "",
  taxId: "",
  companyName: "",
  companyAddress: "",
  numberOfEmployees: "",
  workingDays: "",
  grossSalary: "",
  incorporationDate: null,
};

const defaultAuthorsAndContributorList = {
  name: undefined,
  position: undefined,
};

const defaultAirConditionerList = {
  airConditionerBTU: undefined,
  airConditionerRefrigerant: undefined,
  airConditionerAmount: undefined,
};

const ReportCFOForm = (props) => {
  const { disabled, onClose, selectedFactSheetYear } = props;
  const { control, handleSubmit, setValue, watch, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      authorsAndContributorList: [defaultAuthorsAndContributorList],
      airConditionerList: [defaultAirConditionerList],
    },
  });

  const companyId = useDempStore((state) => state.companyId);
  const selectedYear = dayjs(selectedFactSheetYear).format("YYYY");

  const {
    dataCfoReportFactSheet,
    isLoadingDataCfoReportFactSheet,
    isErrorDataCfoReportFactSheet,
    mutateCfoReportFactSheet: refetchDataCfoReportFactSheet,
  } = useGetCfoReportFactSheet(companyId, selectedYear);

  const setFieldValue = () => {
    if (dataCfoReportFactSheet) {
      const dataGeneral = dataCfoReportFactSheet?.general;
      const dataCfoReport = dataCfoReportFactSheet?.cfo;

      //   format date for general data
      const newIncorporationDate = dataGeneral?.incorporation_date
        ? dayjs(dataGeneral.incorporation_date, "DD-MM-YYYY")
        : null;

      const incorporationDate = newIncorporationDate
        ? {
            day: newIncorporationDate.date(),
            month: newIncorporationDate.month() + 1,
            year: newIncorporationDate.year(),
          }
        : null;

      //   format date for cfo report data
      const newFormattedFiscalYear = {
        start_date: dataCfoReport?.fiscal_period?.start_date
          ? dayjs(dataCfoReport?.fiscal_period?.start_date, "DD-MM-YYYY")
          : null,
        end_date: dataCfoReport?.fiscal_period?.end_date
          ? dayjs(dataCfoReport?.fiscal_period?.end_date, "DD-MM-YYYY")
          : null,
      };

      const newFiscalYear =
        newFormattedFiscalYear.start_date && newFormattedFiscalYear.end_date
          ? {
              startDate: {
                day: newFormattedFiscalYear.start_date.date(),
                month: newFormattedFiscalYear.start_date.month() + 1,
                year: newFormattedFiscalYear.start_date.year(),
              },
              endDate: {
                day: newFormattedFiscalYear.end_date.date(),
                month: newFormattedFiscalYear.end_date.month() + 1,
                year: newFormattedFiscalYear.end_date.year(),
              },
            }
          : null;

      //   reset form data --------------------------------------------------------------------------------
      reset({
        // reset general field
        companyImage: dataGeneral?.images?.org_logo?.imageUrl,
        taxId: dataGeneral?.tax_id,
        companyName: dataGeneral?.name,
        companyAddress: dataGeneral?.address,
        incorporationDate: incorporationDate,
        numberOfEmployees: dataGeneral?.number_of_employees ?? "",
        workingDays: dataGeneral?.working_days ?? "",
        grossSalary: dataGeneral?.gross_salary ?? "",

        // reset cfo report field
        fiscalYear: newFiscalYear,
        organizationChart: dataCfoReport?.documents?.org_chart?.map((file) => ({
          id: file.id,
          name: file.name,
          preview: file.imageUrl,
          type: file.mimeType,
          size: file.size,
          //   remark: file.remark,
        })),
        organizationalScope: dataCfoReport?.documents?.org_scope_doc?.map(
          (file) => ({
            id: file.id,
            name: file.name,
            preview: file.imageUrl,
            type: file.mimeType,
            size: file.size,
            //   remark: file.remark,
          })
        ),
        authorsAndContributorList: dataCfoReport?.contacts?.map((item) => ({
          // id: item.id,
          name: item.name,
          position: item.position,
        })),
        airConditionerList: dataCfoReport?.aircons_btu?.map((item) => ({
          airConditionerBTU: item.capacity_btu,
          airConditionerRefrigerant: groupOptionRefrigerant.find(
            (option) => option.name === item.refrigerant
          )?.id,
          airConditionerAmount: item.count,
        })),
        scopes: dataCfoReport?.scopes,
      });
    } else if (isErrorDataCfoReportFactSheet) {
      reset({
        ...defaultGeneralFactSheet,
        authorsAndContributorList: [defaultAuthorsAndContributorList],
        airConditionerList: [defaultAirConditionerList],
      });
    }
  };

  useEffect(() => {
    setFieldValue();
  }, [dataCfoReportFactSheet, isErrorDataCfoReportFactSheet]);

  const onSubmit = async (formData) => {
    const newFormattedFiscalYear = {
      start_date: moment({
        ...formData?.fiscalYear?.startDate,
        month: formData?.fiscalYear?.startDate?.month - 1,
      }).format("DD-MM-YYYY"),
      end_date: moment({
        ...formData?.fiscalYear?.endDate,
        month: formData?.fiscalYear?.endDate?.month - 1,
      }).format("DD-MM-YYYY"),
    };

    const data = {
      fiscal_period: newFormattedFiscalYear,
      contacts: formData?.authorsAndContributorList,
      aircons_btu: formData?.airConditionerList.map((item) => ({
        capacity_btu: item.airConditionerBTU,
        refrigerant: item.airConditionerRefrigerant
          ? groupOptionRefrigerant.find(
              (option) => option.id === Number(item.airConditionerRefrigerant)
            )?.name
          : undefined,
        count: item.airConditionerAmount,
      })),
      scopes: formData?.scopes,
    };

    // the logic for delete image org chart and org scope doc --------------------------
    const defaultAttachmentOrgChartIds =
      dataCfoReportFactSheet?.cfo?.documents?.org_chart?.map(
        (file) => file.id
      ) ?? [];

    const defaultAttachmentOrgScopeDocIds =
      dataCfoReportFactSheet?.cfo?.documents?.org_scope_doc?.map(
        (file) => file.id
      ) ?? [];

    const currentAttachmentsOrgChartIds =
      formData?.organizationChart?.map((file) => file?.id) ?? [];

    const currentAttachmentsOrgScopeDocIds =
      formData?.organizationalScope?.map((file) => file?.id) ?? [];

    const deletedImageOrgChartIds = defaultAttachmentOrgChartIds?.filter(
      (id) => !currentAttachmentsOrgChartIds.includes(id)
    );

    const deletedImageOrgScopeDocIds = defaultAttachmentOrgScopeDocIds?.filter(
      (id) => !currentAttachmentsOrgScopeDocIds.includes(id)
    );

    const deletedImageIds = [
      ...deletedImageOrgChartIds,
      ...deletedImageOrgScopeDocIds,
    ];

    // --------------------------------------------------------------------------------

    const payload = {
      company_id: companyId,
      year: Number(selectedYear),
      org_chart: formData?.organizationChart,
      org_scope_doc: formData?.organizationalScope,
      data: data,
      del_image_ids: deletedImageIds,
    };

    const response = await createCfoReportFactSheet(payload);

    if (response?.data) {
      await refetchDataCfoReportFactSheet();
      onClose();
    } else if (response?.error) {
      console.log(response?.error);
    }
  };

  return (
    <div className="w-full h-full p-6">
      <div className="relative max-w-[87.5rem] h-full mx-auto">
        <div
          className={`flex flex-col gap-6 w-full h-[calc(100%-53px)] ${clsx({
            "h-[calc(100%-53px)]": !disabled,
          })}`}
        >
          {/* main information will must disabled is always true when on report cfo form page*/}
          <MainInformation control={control} disabled={true} required={false} />

          <FiscalYearForm control={control} disabled={disabled} />

          <OrganizationChartForm control={control} disabled={disabled} />

          <OrganizationalScopeForm control={control} disabled={disabled} />

          <AuthorsAndContributorListsForm
            control={control}
            watch={watch}
            defaultAuthorsAndContributorList={defaultAuthorsAndContributorList}
            disabled={disabled}
          />

          <AirConditionerListsForm
            control={control}
            watch={watch}
            defaultAirConditionerList={defaultAirConditionerList}
            disabled={disabled}
          />

          <CheckBoxListsForm control={control} disabled={disabled} />
        </div>

        {/* button actions */}
        {!disabled && (
          <div
            // className={`w-full flex items-center justify-center gap-5 pt-4 absolute bottom-0 left-0 border-t border-gray-7`}
            className={`w-full flex items-center justify-center gap-5 pt-4 mt-4 border-t border-gray-7`}
          >
            <BaseButton
              variant="primary"
              size="normal"
              className="!min-w-[100px]"
              onClick={handleSubmit((formData) => onSubmit(formData))}
            >
              Save
            </BaseButton>

            <BaseButton
              variant="secondary"
              size="normal"
              className="!min-w-[100px]"
              onClick={() => {
                onClose();
                setFieldValue();
              }}
            >
              Cancel
            </BaseButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCFOForm;
